import * as React from 'react';
import { Box, Icon, UtilityText } from '@leagueplatform/genesis-core';
import { SelectActionMenu } from '@web-config-app/core-react-ui';

export interface SourceOption {
  /** `subjectType` value stored on the condition when this source is picked. */
  value: string;
  /** Already-localized display label. */
  label: string;
}

interface SourcePickerProps {
  /** The currently selected `subjectType`. */
  value: string;
  options: SourceOption[];
  /** Accessible name for the trigger (there is no visible field label). */
  menuLabel: string;
  enabled: boolean;
  onChange: (value: string) => void;
}

// Mirrors the static subject `Pill` look (filled, fully-rounded, subdued),
// plus a chevron so it reads as interactive. The button reset (appearance /
// font / margin) is what keeps it a compact chip: without it the native button
// chrome inflates the pill into an oversized oval. `paddingInlineEnd` is
// tighter than the leading pad so the text sits snug against the chevron.
const triggerCss = {
  appearance: 'none',
  border: 'none',
  margin: 0,
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '$quarter',
  paddingBlock: '$quarter',
  paddingInlineStart: '$half',
  paddingInlineEnd: '$quarter',
  borderRadius: '$pill',
  backgroundColor: '$surfaceBackgroundSecondary',
  color: '$onSurfaceTextSubdued',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:disabled': { cursor: 'default' },
  '&:focus-visible': {
    outline: '2px solid $interactiveFocusOuter',
    outlineOffset: '-2px',
  },
} as const;

/**
 * Pill-styled trigger for the source action menu. `DropdownMenu.Trigger asChild`
 * clones this and injects its own props + ref, so it must forward both onto the
 * native `<button>`.
 */
const SourceTrigger = React.forwardRef<
  HTMLButtonElement,
  { label: string; menuLabel: string; disabled?: boolean }
>(({ label, menuLabel, disabled, ...triggerProps }, ref) => (
  <Box
    as="button"
    type="button"
    ref={ref}
    // Include the visible selection text in the accessible name (WCAG 2.5.3):
    // `aria-label={menuLabel}` alone would override "Question"/"Data point",
    // leaving speech-control users unable to target it by its visible word.
    aria-label={`${menuLabel}: ${label}`}
    disabled={disabled}
    css={triggerCss}
    {...triggerProps}
  >
    <UtilityText css={{ color: '$onSurfaceTextSubdued' }}>{label}</UtilityText>
    <Icon
      icon="interfaceChevronDown"
      size="$one"
      css={{ color: '$onSurfaceTextSubdued' }}
    />
  </Box>
));

/**
 * Picks a condition's SOURCE — which subject variant it tests (e.g. a
 * question vs. a member data point). Rendered as a pill-styled action picker
 * so it reads as a token in the condition sentence rather than a form field;
 * selecting an option swaps the condition's `subjectType`, which drives the
 * dependent subject picker in the row.
 */
export const SourcePicker: React.FC<SourcePickerProps> = ({
  value,
  options,
  menuLabel,
  enabled,
  onChange,
}) => {
  const activeLabel =
    options.find((option) => option.value === value)?.label ?? '';

  // Read-only view keeps the pill look but drops the interactive menu.
  if (!enabled) {
    return <SourceTrigger label={activeLabel} menuLabel={menuLabel} disabled />;
  }

  const menuGroups = [
    {
      menuItems: options.map((option) => ({
        id: option.value,
        label: option.label,
        selected: option.value === value,
        action: () => onChange(option.value),
      })),
    },
  ];

  // `SelectActionMenu` wraps its trigger in a horizontal `StackLayout` that
  // grows to fill the row; left unchecked it eats the space between this pill
  // and the following picker, shoving a narrow picker (e.g. the Age select) to
  // the far right. Contain it in a shrink-to-content box so the pill hugs its
  // label and the dependent picker sits directly beside it.
  return (
    <Box css={{ display: 'inline-flex', flexShrink: 0 }}>
      <SelectActionMenu menuGroups={menuGroups}>
        <SourceTrigger label={activeLabel} menuLabel={menuLabel} />
      </SelectActionMenu>
    </Box>
  );
};
